/**
 * Buy X Get Y auto-gift engine.
 *
 * Reads `bogo_offer` blocks rendered server-side as `[data-bogo-offer]`
 * elements. Each offer can be triggered by a subtotal threshold, a specific
 * product being in the cart, or both (AND). On every cart change it:
 *   - adds the configured gift variant when the trigger condition(s) are met
 *   - removes it when the cart no longer qualifies
 *   - updates each offer's progress bar label + fill
 *
 * The Shopify "Buy X Get Y" automatic discount only applies if the Y item is
 * already in the cart, so this script makes sure it is.
 */
(function () {
  const SOURCE = 'bogo';
  const STORAGE_PREFIX = 'bogo:dismissed:';
  const GIFT_PROPERTY_KEY = '_bogo_code';

  let inflight = null;
  let dirty = false;
  let lastCart = null;

  function readOffers() {
    return Array.from(document.querySelectorAll('[data-bogo-offer]')).map((el) => ({
      el,
      code: el.dataset.bogoCode,
      threshold: parseInt(el.dataset.bogoThreshold, 10) || 0,
      triggerProductId: parseInt(el.dataset.bogoTriggerProductId, 10) || 0,
      triggerMinQty: parseInt(el.dataset.bogoTriggerMinQty, 10) || 1,
      triggerProductTitle: el.dataset.bogoTriggerProductTitle || '',
      variantId: parseInt(el.dataset.bogoVariantId, 10) || 0,
      variantAvailable: el.dataset.bogoVariantAvailable === 'true',
      productTitle: el.dataset.bogoProductTitle || '',
      preTemplate: el.dataset.bogoPreTemplate || '',
      postTemplate: el.dataset.bogoPostTemplate || '',
    }));
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  }

  function findGiftLine(cart, offer) {
    if (!cart || !cart.items) return null;
    for (let i = 0; i < cart.items.length; i++) {
      const item = cart.items[i];
      const props = item.properties || {};
      if (props[GIFT_PROPERTY_KEY] === offer.code) {
        return { item, line: i + 1 };
      }
    }
    return null;
  }

  // Collects the set of variant IDs configured as gifts across all offers on
  // the page. Used to exclude gift lines from threshold/trigger calculations
  // regardless of properties — defends against orphan gift lines and other
  // systems (Rebuy, free-gift apps) that may add the gift product without our
  // `_bogo_gift` marker, which would otherwise inflate the eligible subtotal.
  function giftVariantSet(offers) {
    const set = new Set();
    if (!offers) return set;
    for (const offer of offers) {
      if (offer.variantId) set.add(offer.variantId);
    }
    return set;
  }

  function isGiftLine(item, giftVariantIds) {
    const props = item.properties || {};
    if (props._bogo_gift === 'true') return true;
    if (giftVariantIds && giftVariantIds.has(item.variant_id)) return true;
    return false;
  }

  // Subtotal that counts only the customer's paid items. We sum non-gift
  // line_prices directly instead of subtracting from items_subtotal_price —
  // once Shopify's Buy X Get Y discount applies, the gift's line_price is 0
  // and items_subtotal_price already excludes it, so subtracting again would
  // double-count and falsely drop us below threshold (causing the gift to be
  // removed and re-added on every cart event).
  function eligibleSubtotal(cart, giftVariantIds) {
    if (!cart || !cart.items) return 0;
    let total = 0;
    for (const item of cart.items) {
      if (isGiftLine(item, giftVariantIds)) continue;
      total += item.line_price || 0;
    }
    return total;
  }

  function isDismissed(code) {
    try {
      return sessionStorage.getItem(STORAGE_PREFIX + code) === '1';
    } catch (_) {
      return false;
    }
  }

  function setDismissed(code, value) {
    try {
      if (value) sessionStorage.setItem(STORAGE_PREFIX + code, '1');
      else sessionStorage.removeItem(STORAGE_PREFIX + code);
    } catch (_) {}
  }

  // Sums quantities of any variant of the trigger product currently in the
  // cart, excluding gift lines. Returns 0 if no trigger is configured.
  function triggerProductCount(cart, productId, giftVariantIds) {
    if (!productId || !cart || !cart.items) return 0;
    let count = 0;
    for (const item of cart.items) {
      if (isGiftLine(item, giftVariantIds)) continue;
      if (item.product_id === productId) count += item.quantity || 0;
    }
    return count;
  }

  // Returns true if a line with the offer's gift variant exists in the cart
  // for any reason — ours, an external app's, or the customer manually adding
  // it. Used to avoid creating duplicate gift lines.
  function giftVariantPresent(cart, offer) {
    if (!cart || !cart.items || !offer.variantId) return false;
    return cart.items.some((item) => item.variant_id === offer.variantId);
  }

  function offerQualifies(offer, cart, giftVariantIds) {
    if (!offer.threshold && !offer.triggerProductId) return false;
    const subtotalOk = !offer.threshold || eligibleSubtotal(cart, giftVariantIds) >= offer.threshold;
    const productOk = !offer.triggerProductId || triggerProductCount(cart, offer.triggerProductId, giftVariantIds) >= offer.triggerMinQty;
    return subtotalOk && productOk;
  }

  function decide(offer, cart, giftVariantIds) {
    if (!offer.code || !offer.variantId) return 'noop';
    // At least one trigger condition must be configured.
    if (!offer.threshold && !offer.triggerProductId) return 'noop';

    const qualifies = offerQualifies(offer, cart, giftVariantIds);
    const hasOurGift = !!findGiftLine(cart, offer);
    const variantAlreadyInCart = giftVariantPresent(cart, offer);

    if (!qualifies && hasOurGift) return 'remove';
    // Don't add if the variant is already in the cart for any reason — avoids
    // creating duplicate gift lines if another system added the same product.
    if (qualifies && !variantAlreadyInCart && !isDismissed(offer.code) && offer.variantAvailable) return 'add';
    if (!qualifies && isDismissed(offer.code)) {
      // Conditions dropped — clear dismissal so a future re-cross re-adds the gift.
      setDismissed(offer.code, false);
    }
    return 'noop';
  }

  async function addGift(offer) {
    const body = JSON.stringify({
      items: [
        {
          id: offer.variantId,
          quantity: 1,
          properties: {
            _bogo_code: offer.code,
            _bogo_gift: 'true',
          },
        },
      ],
    });
    const res = await fetch('/cart/add.js', { ...fetchConfig(), body });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error('bogo: add failed (' + res.status + ') ' + text);
    }
    return res.json();
  }

  async function removeGift(cart, offer) {
    const found = findGiftLine(cart, offer);
    if (!found) return null;
    const body = JSON.stringify({ line: found.line, quantity: 0 });
    const res = await fetch('/cart/change.js', { ...fetchConfig(), body });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error('bogo: remove failed (' + res.status + ') ' + text);
    }
    return res.json();
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    if (window.Shopify && typeof Shopify.formatMoney === 'function' && window.Shopify.money_format) {
      try {
        return Shopify.formatMoney(cents, window.Shopify.money_format);
      } catch (_) {}
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function renderProgressBars(cart) {
    const offers = readOffers();
    const giftVariantIds = giftVariantSet(offers);
    const subtotal = eligibleSubtotal(cart, giftVariantIds);
    offers.forEach((offer) => {
      const labelEl = offer.el.querySelector('[data-bogo-label]');
      const fillEl = offer.el.querySelector('[data-bogo-fill]');
      if (!labelEl || !fillEl) return;

      const qualifies = offerQualifies(offer, cart, giftVariantIds);
      const triggerCount = triggerProductCount(cart, offer.triggerProductId, giftVariantIds);
      const remaining = offer.threshold ? Math.max(offer.threshold - subtotal, 0) : 0;
      const qtyRemaining = offer.triggerProductId ? Math.max(offer.triggerMinQty - triggerCount, 0) : 0;

      // Progress fill follows whichever trigger is configured. If only a product
      // trigger is set, the bar is binary (empty or full). If both are configured,
      // we show subtotal progress since that's the most natural metric to fill.
      let pct;
      if (offer.threshold) {
        pct = Math.max(0, Math.min(100, Math.round((subtotal / offer.threshold) * 100)));
      } else {
        pct = qualifies ? 100 : 0;
      }

      const template = qualifies ? offer.postTemplate : offer.preTemplate;
      labelEl.textContent = (template || '')
        .replace('[remaining]', formatMoney(remaining))
        .replace('[qty]', String(qtyRemaining))
        .replace('[product]', offer.productTitle)
        .replace('[trigger]', offer.triggerProductTitle);
      fillEl.style.width = pct + '%';
      offer.el.classList.toggle('is-unlocked', qualifies);
    });
  }

  function detectManualRemoval(prev, next, offers, giftVariantIds) {
    if (!prev) return;
    offers.forEach((offer) => {
      const prevHad = !!findGiftLine(prev, offer);
      const nextHas = !!findGiftLine(next, offer);
      if (prevHad && !nextHas && offerQualifies(offer, next, giftVariantIds)) {
        setDismissed(offer.code, true);
      }
    });
  }

  async function runReconcile() {
    const offers = readOffers();
    if (offers.length === 0) return;
    const giftVariantIds = giftVariantSet(offers);

    let cart = await fetchCart();
    detectManualRemoval(lastCart, cart, offers, giftVariantIds);

    let mutated = false;
    for (const offer of offers) {
      const action = decide(offer, cart, giftVariantIds);
      if (action === 'add') {
        // Final guard: re-check qualification right before mutating. Belt and
        // suspenders against any state drift between decide() and here.
        if (!offerQualifies(offer, cart, giftVariantIds)) continue;
        try {
          await addGift(offer);
          mutated = true;
          cart = await fetchCart();
        } catch (e) {
          console.warn('[bogo]', e);
        }
      } else if (action === 'remove') {
        try {
          await removeGift(cart, offer);
          mutated = true;
          cart = await fetchCart();
        } catch (e) {
          console.warn('[bogo]', e);
        }
      }
    }

    if (mutated) {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: cart });
      }
      syncCartStatsProxy(cart);
    }

    renderProgressBars(cart);
    lastCart = cart;
  }

  // Mirror what _helpers/cart.js#checkCart does — keep external displays
  // ([data-display-subtotal], cart count badge, etc.) in sync after our mutations.
  function syncCartStatsProxy(cart) {
    const stats = window.Zero && window.Zero.Cart && window.Zero.Cart.stats;
    if (!stats) return;
    if (stats.count !== cart.item_count) stats.count = cart.item_count;
    if (stats.discount !== cart.total_discount) stats.discount = cart.total_discount;
    if (stats.subtotal !== cart.items_subtotal_price) stats.subtotal = cart.items_subtotal_price;
    if (stats.total !== cart.total_price) stats.total = cart.total_price;
  }

  function reconcile() {
    if (inflight) {
      dirty = true;
      return inflight;
    }
    inflight = (async () => {
      try {
        await runReconcile();
        while (dirty) {
          dirty = false;
          await runReconcile();
        }
      } catch (e) {
        console.warn('[bogo] reconcile error', e);
      } finally {
        inflight = null;
      }
    })();
    return inflight;
  }

  // Hook window.fetch so we reconcile after ANY cart mutation, regardless of
  // which code path made it. The theme runs custom product-form code, Rebuy
  // SmartCart, and other third-party fetch wrappers — none of which reliably
  // publish to PUB_SUB_EVENTS.cartUpdate, so subscribing alone misses real adds.
  // The hook detects /cart/add, /cart/change, /cart/update calls. Bogo's own
  // mutations are also detected, but the inflight+dirty queue in reconcile()
  // dedupes the extra pass into a no-op fetchCart.
  function installFetchHook() {
    if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
    if (window.fetch.__bogoHooked) return;
    const original = window.fetch;
    const wrapped = function bogoFetchHook(input, init) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const result = original.apply(this, arguments);
      if (/\/cart\/(add|change|update)(\.js|\.json|\?|$)/.test(url)) {
        result.then(
          (res) => {
            if (!res || !res.ok) return;
            // Defer behind the caller's own .then handlers so they consume
            // the body first; reconcile() does its own /cart.js fetch anyway.
            Promise.resolve().then(reconcile);
          },
          () => {}
        );
      }
      return result;
    };
    wrapped.__bogoHooked = true;
    window.fetch = wrapped;
  }

  function init() {
    const offers = readOffers();
    if (offers.length === 0) {
      console.warn('[bogo] No [data-bogo-offer] elements in the DOM. Add a BOGO Offer block to the BOGO offers section in the customizer and pick a gift product.');
      return;
    }

    installFetchHook();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.source === SOURCE) return;
        reconcile();
      });
    }

    reconcile();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
